import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import {
  ArrowLeftIcon,
  CalendarIcon,
  CheckCircledIcon,
  CheckIcon,
  Cross2Icon,
  FileTextIcon,
  LayersIcon,
  Pencil1Icon,
  PersonIcon,
  PlusIcon,
  RocketIcon,
  StackIcon,
  TrashIcon,
  LockClosedIcon,
  LockOpen1Icon,
} from '@radix-ui/react-icons';
import Navbar from '../components/Navbar';
import EscalaGrade from '../components/escala/EscalaGrade';
import CicloFormModal from '../components/escala/CicloFormModal';
import DiaEditorModal from '../components/escala/DiaEditorModal';
import ConfirmModal from '../components/escala/ConfirmModal';
import Stepper from '../components/escala/Stepper';
import {
  CICLO_STATUS_DESC,
  CICLO_STATUS_LABEL,
  DIAS_OPCAO_LABEL,
  DIAS_SEMANA_CURTO,
  MODO_DESC,
  MODO_LABEL,
  TURNO_LABEL,
  diasOpcaoDe,
  fimDeSemanaComVaga,
  formatDataCurta,
  formatDataHora,
  mensagemErro,
  nomeEscala,
  periodoLabel,
  turnosDoCiclo,
} from '../components/escala/escalaUtils';
import {
  aprovarPendentes,
  atualizarCiclo,
  atualizarDia,
  atualizarDiasEmLote,
  criarCiclo,
  decidirInscricao,
  escalar,
  excluirCiclo,
  listarCandidatos,
  listarCiclos,
  obterCiclo,
} from '../services/escalaApi';
import './Global.css';
import './Escala.css';

/**
 * Tela do admin: lista de ciclos e, dentro de um ciclo, o calendário para
 * montar a escala (vagas, feriados, escalar/aprovar), a fila de pendências
 * e a carga por pessoa. A versão para imprimir fica em /escala/:id/impressao.
 */
const EscalaAdminPage = () => {
  const { cicloId } = useParams();
  const navigate = useNavigate();

  const [ciclos, setCiclos] = useState([]);
  const [ciclo, setCiclo] = useState(null);
  const [candidatos, setCandidatos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [aba, setAba] = useState('calendario');
  const [mostrarFds, setMostrarFds] = useState(null); // null = automático
  const [modalCiclo, setModalCiclo] = useState(null); // 'novo' | 'editar'
  const [diaAbertoId, setDiaAbertoId] = useState(null);
  const [modalLote, setModalLote] = useState(false);
  const [lote, setLote] = useState({ vagasManha: 4, vagasTarde: 4, vagasNoite: 0, escopo: 'padrao' });
  const [confirm, setConfirm] = useState(null); // { titulo, texto, label, perigo, acao }

  // ---------------- carga ----------------
  const carregarLista = useCallback(async () => {
    try {
      setCiclos(await listarCiclos());
    } catch (e) {
      toast.error(mensagemErro(e, 'Erro ao carregar os ciclos.'));
    }
  }, []);

  const carregarCiclo = useCallback(async (id) => {
    try {
      const [detalhe, cand] = await Promise.all([obterCiclo(id), listarCandidatos(id)]);
      setCiclo(detalhe);
      setCandidatos(cand);
    } catch (e) {
      toast.error(mensagemErro(e, 'Erro ao carregar o ciclo.'));
      navigate('/admin/escala');
    }
  }, [navigate]);

  useEffect(() => {
    let vivo = true;
    (async () => {
      setLoading(true);
      await carregarLista();
      if (cicloId) await carregarCiclo(cicloId);
      else setCiclo(null);
      if (vivo) setLoading(false);
    })();
    return () => {
      vivo = false;
    };
  }, [cicloId, carregarLista, carregarCiclo]);

  const turnos = useMemo(() => (ciclo ? turnosDoCiclo(ciclo.dias) : []), [ciclo]);
  const fdsAuto = useMemo(() => (ciclo ? fimDeSemanaComVaga(ciclo.dias) : { sabado: false, domingo: false }), [ciclo]);
  // null = automático (mostra as colunas que têm vaga); true = força as duas.
  const mostrar = mostrarFds === null ? fdsAuto : { sabado: mostrarFds, domingo: mostrarFds };
  const mostrarFdsEfetivo = mostrar.sabado && mostrar.domingo;
  const diaAberto = useMemo(
    () => (ciclo && diaAbertoId ? ciclo.dias.find((d) => d.id === diaAbertoId) : null),
    [ciclo, diaAbertoId],
  );

  const pendencias = useMemo(() => {
    if (!ciclo) return [];
    const out = [];
    for (const dia of ciclo.dias) {
      for (const turno of dia.turnos) {
        (turno.pendentes || []).forEach((p, idx) =>
          out.push({ ...p, dia, turno, posicao: idx + 1, cheio: turno.livres === 0 }),
        );
      }
    }
    return out;
  }, [ciclo]);

  // ---------------- ações ----------------
  const executar = async (fn, { sucesso, recarregar = true } = {}) => {
    setBusy(true);
    try {
      const r = await fn();
      if (sucesso) toast.success(typeof sucesso === 'function' ? sucesso(r) : sucesso);
      if (r && r.promovidos > 0) {
        toast(`${r.promovidos} pedido(s) da fila confirmado(s) automaticamente.`, { icon: '⬆️' });
      }
      if (recarregar && ciclo) await carregarCiclo(ciclo.id);
      await carregarLista();
      return r;
    } catch (e) {
      toast.error(mensagemErro(e), { duration: 7000 });
      return null;
    } finally {
      setBusy(false);
    }
  };

  const criar = async (dados) => {
    const r = await executar(() => criarCiclo(dados), { sucesso: 'Ciclo criado em rascunho. Agora ajuste os dias e publique.', recarregar: false });
    if (r) {
      setModalCiclo(null);
      navigate(`/admin/escala/${r.id}`);
    }
  };

  const editar = async (dados) => {
    const r = await executar(() => atualizarCiclo(ciclo.id, dados), { sucesso: 'Ciclo atualizado.' });
    if (r) setModalCiclo(null);
  };

  const mudarStatus = (status) => {
    const acao = () =>
      executar(() => atualizarCiclo(ciclo.id, { status }), {
        sucesso: {
          publicado: 'Escala publicada — os itinerantes já podem pedir vagas.',
          encerrado: 'Escala encerrada.',
          rascunho: 'Ciclo voltou para rascunho (os itinerantes não o veem).',
        }[status],
      });
    if (status === 'publicado' && ciclo.status === 'rascunho') return acao();
    setConfirm({
      titulo: { encerrado: 'Encerrar escala?', rascunho: 'Voltar para rascunho?', publicado: 'Reabrir escala?' }[status],
      texto: {
        encerrado: 'Ninguém mais pede vaga nem desiste. Você continua podendo escalar e remover pessoas.',
        rascunho: 'Os itinerantes deixam de ver o ciclo. As inscrições existentes são mantidas.',
        publicado: 'Os itinerantes voltam a ver o calendário e a pedir vagas.',
      }[status],
      label: { encerrado: 'Encerrar', rascunho: 'Voltar para rascunho', publicado: 'Reabrir' }[status],
      perigo: status !== 'publicado',
      acao: async () => {
        await acao();
        setConfirm(null);
      },
    });
  };

  const mudarModo = (modo) => {
    if (!ciclo || modo === ciclo.modo) return;
    const acao = () => executar(() => atualizarCiclo(ciclo.id, { modo }), { sucesso: `Modo alterado para "${MODO_LABEL[modo]}".` });
    if (modo === 'ordem_chegada' && ciclo.resumo.pendentes > 0) {
      setConfirm({
        titulo: 'Mudar para ordem de chegada?',
        texto: `Há ${ciclo.resumo.pendentes} pedido(s) aguardando. Ao mudar, eles são confirmados automaticamente na ordem em que chegaram, até preencher as vagas de cada turno.`,
        label: 'Mudar e confirmar a fila',
        acao: async () => {
          await acao();
          setConfirm(null);
        },
      });
      return;
    }
    acao();
  };

  const excluir = () =>
    setConfirm({
      titulo: 'Excluir ciclo?',
      texto: `"${ciclo.nome}" e todos os seus dias e inscrições serão apagados. Isso não pode ser desfeito.`,
      label: 'Excluir',
      perigo: true,
      acao: async () => {
        const r = await executar(() => excluirCiclo(ciclo.id), { sucesso: 'Ciclo excluído.', recarregar: false });
        setConfirm(null);
        if (r) navigate('/admin/escala');
      },
    });

  const salvarDia = (dados) =>
    executar(() => atualizarDia(ciclo.id, diaAbertoId, dados), { sucesso: 'Dia salvo.' });

  const escalarPessoa = (turno, userId) =>
    executar(() => escalar(ciclo.id, diaAbertoId, turno, userId), {
      sucesso: (r) => `${nomeEscala(r)} escalado(a) na ${TURNO_LABEL[turno].toLowerCase()}.`,
    });

  const decidir = (inscricao, status, observacao) =>
    executar(() => decidirInscricao(inscricao.id, status, observacao), {
      sucesso: {
        confirmado: `${nomeEscala(inscricao)} confirmado(a).`,
        recusado: `Pedido de ${nomeEscala(inscricao)} recusado.`,
        cancelado: `${nomeEscala(inscricao)} removido(a) da escala.`,
        pendente: `${nomeEscala(inscricao)} voltou para a fila.`,
      }[status],
    });

  const aprovarTodos = () =>
    setConfirm({
      titulo: 'Confirmar pedidos na ordem de chegada?',
      texto: 'Cada turno confirma quem pediu primeiro, até preencher as vagas. Quem não couber continua aguardando.',
      label: 'Confirmar os que cabem',
      acao: async () => {
        const r = await executar(() => aprovarPendentes(ciclo.id), {
          sucesso: (x) => (x.promovidos > 0 ? `${x.promovidos} pedido(s) confirmado(s).` : 'Nenhum pedido cabia nas vagas livres.'),
        });
        setConfirm(null);
        return r;
      },
    });

  const aplicarLote = async () => {
    const r = await executar(
      () =>
        atualizarDiasEmLote(ciclo.id, {
          escopo: lote.escopo,
          vagasManha: Number(lote.vagasManha),
          vagasTarde: Number(lote.vagasTarde),
          vagasNoite: Number(lote.vagasNoite),
        }),
      { sucesso: (x) => `Vagas aplicadas em ${x.atualizados} dia(s).` },
    );
    if (r) setModalLote(false);
  };

  // ---------------- render helpers ----------------
  const renderTurnoAdmin = (dia, turno, t) => {
    const cheio = turno.livres === 0 && turno.vagas > 0;
    return (
      <>
        <div className="esc-turno-cab">
          <span className="esc-turno-nome">{t.label}</span>
          {turno.vagas > 0 ? (
            <span className={`esc-cap ${cheio ? 'cheio' : ''}`}>
              {turno.confirmados.length}/{turno.vagas}
            </span>
          ) : (
            <span className="esc-cap off">—</span>
          )}
        </div>
        {turno.vagas > 0 && (
          <ul className="esc-nomes">
            {turno.confirmados.map((c) => (
              <li key={c.id} className="esc-nome ok" title={`${nomeEscala(c, true)} · ${c.origem === 'admin' ? 'escalado' : 'pediu a vaga'}`}>
                {nomeEscala(c)}
              </li>
            ))}
            {Array.from({ length: turno.livres }).map((_, i) => (
              <li key={`l${i}`} className="esc-nome livre">
                vaga livre
              </li>
            ))}
          </ul>
        )}
        {turno.pendentesCount > 0 && (
          <span className="esc-fila-badge">{turno.pendentesCount} aguardando</span>
        )}
      </>
    );
  };

  // ---------------- views ----------------
  if (loading) {
    return (
      <>
        <Navbar />
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Carregando…</p>
        </div>
      </>
    );
  }

  const ciclosExistentes = ciclos.map((c) => ({ dataInicio: c.dataInicio, dataFim: c.dataFim }));

  // ======== LISTA ========
  if (!ciclo) {
    return (
      <>
        <Toaster position="top-right" />
        <Navbar />
        <div className="container esc-page">
          <div className="page-header-new">
            <div>
              <h1>Escala de Itinerantes</h1>
              <p className="subtitle">Monte a escala mensal dentro do sistema: vagas por turno, feriados e quem vai em cada dia</p>
            </div>
            <div className="header-actions">
              <button className="btn-primary" onClick={() => setModalCiclo('novo')}>
                <PlusIcon /> Novo ciclo
              </button>
            </div>
          </div>

          {ciclos.length === 0 ? (
            <div className="esc-vazio">
              <div className="esc-vazio-icone">
                <CalendarIcon width={40} height={40} />
              </div>
              <h2>Nenhum ciclo ainda</h2>
              <p>
                Crie o primeiro ciclo (por padrão o período de pagamento 16 → 15). Os dias são gerados com as
                vagas padrão; depois é só marcar feriados, ajustar vagas e publicar para os itinerantes pedirem.
              </p>
              <button className="btn-primary" onClick={() => setModalCiclo('novo')}>
                <PlusIcon /> Criar ciclo
              </button>
            </div>
          ) : (
            <div className="esc-ciclos">
              {ciclos.map((c) => (
                <Link key={c.id} to={`/admin/escala/${c.id}`} className={`esc-ciclo-card status-${c.status}`}>
                  <div className="esc-ciclo-card-topo">
                    <span className={`esc-pill status-${c.status}`}>{CICLO_STATUS_LABEL[c.status]}</span>
                    {c.emCurso && <span className="esc-pill em-curso">em curso</span>}
                    <span className="esc-pill modo">{MODO_LABEL[c.modo]}</span>
                  </div>
                  <h3>{c.nome}</h3>
                  <p className="esc-ciclo-periodo">{periodoLabel(c)}</p>
                  <div className="esc-ciclo-kpis">
                    <span><strong>{c.resumo.confirmados}</strong> confirmados</span>
                    <span><strong>{c.resumo.vagasTotal - c.resumo.confirmados}</strong> livres</span>
                    <span className={c.resumo.pendentes > 0 ? 'destaque' : ''}><strong>{c.resumo.pendentes}</strong> aguardando</span>
                  </div>
                  <div className="esc-ciclo-barra">
                    <span style={{ width: `${c.resumo.vagasTotal ? Math.min(100, (100 * c.resumo.confirmados) / c.resumo.vagasTotal) : 0}%` }} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
        <CicloFormModal
          aberto={modalCiclo === 'novo'}
          ciclo={null}
          ciclosExistentes={ciclosExistentes}
          busy={busy}
          onClose={() => setModalCiclo(null)}
          onSalvar={criar}
        />
      </>
    );
  }

  // ======== DETALHE ========
  const r = ciclo.resumo;
  const ocupacao = r.vagasTotal ? Math.round((100 * r.confirmados) / r.vagasTotal) : 0;

  return (
    <>
      <Toaster position="top-right" />
      <Navbar />
      <div className="container esc-page">
        <Link to="/admin/escala" className="esc-voltar">
          <ArrowLeftIcon /> Todos os ciclos
        </Link>

        <div className={`esc-hero status-${ciclo.status}`}>
          <div className="esc-hero-conteudo">
            <p className="esc-hero-sobre">Escala de Monitores Itinerantes</p>
            <h1>{ciclo.nome}</h1>
            <div className="esc-hero-meta">
              <span className="esc-hero-chip"><CalendarIcon /> {periodoLabel(ciclo)}</span>
              <span className={`esc-hero-chip status-${ciclo.status}`}>{CICLO_STATUS_LABEL[ciclo.status]}</span>
              {ciclo.emCurso && <span className="esc-hero-chip">em curso</span>}
              <span className="esc-hero-chip">{DIAS_OPCAO_LABEL[diasOpcaoDe(ciclo)]}</span>
            </div>
            <p className="esc-hero-dica">{CICLO_STATUS_DESC[ciclo.status]}</p>
          </div>
          <div className="esc-hero-acoes">
            {ciclo.status === 'rascunho' && (
              <button className="esc-btn-hero primario" onClick={() => mudarStatus('publicado')} disabled={busy}>
                <RocketIcon /> Publicar
              </button>
            )}
            {ciclo.status === 'publicado' && (
              <button className="esc-btn-hero" onClick={() => mudarStatus('encerrado')} disabled={busy}>
                <LockClosedIcon /> Encerrar
              </button>
            )}
            {ciclo.status === 'encerrado' && (
              <button className="esc-btn-hero" onClick={() => mudarStatus('publicado')} disabled={busy}>
                <LockOpen1Icon /> Reabrir
              </button>
            )}
            {ciclo.status === 'publicado' && (
              <button className="esc-btn-hero" onClick={() => mudarStatus('rascunho')} disabled={busy} title="Esconder dos itinerantes">
                Voltar a rascunho
              </button>
            )}
            <Link to={`/escala/${ciclo.id}/impressao`} className="esc-btn-hero">
              <FileTextIcon /> Imprimir
            </Link>
            <button className="esc-btn-hero" onClick={() => setModalCiclo('editar')} disabled={busy}>
              <Pencil1Icon /> Editar
            </button>
            <button className="esc-btn-hero perigo" onClick={excluir} disabled={busy} title="Excluir ciclo">
              <TrashIcon />
            </button>
          </div>
        </div>

        <div className="esc-kpis">
          <div className="esc-kpi">
            <span className="esc-kpi-rotulo">Vagas</span>
            <span className="esc-kpi-valor">{r.vagasTotal}</span>
            <span className="esc-kpi-sub">{r.dias} dias</span>
          </div>
          <div className="esc-kpi ok">
            <span className="esc-kpi-rotulo">Confirmados</span>
            <span className="esc-kpi-valor">{r.confirmados}</span>
            <span className="esc-kpi-sub">{ocupacao}% preenchido</span>
          </div>
          <div className="esc-kpi livre">
            <span className="esc-kpi-rotulo">Livres</span>
            <span className="esc-kpi-valor">{r.livres}</span>
            <span className="esc-kpi-sub">vagas sem ninguém</span>
          </div>
          <div className={`esc-kpi ${r.pendentes > 0 ? 'atencao' : ''}`}>
            <span className="esc-kpi-rotulo">Aguardando</span>
            <span className="esc-kpi-valor">{r.pendentes}</span>
            <span className="esc-kpi-sub">{r.pendentes > 0 ? 'pedidos para decidir' : 'nenhum pedido'}</span>
          </div>
        </div>

        <div className="esc-modo-painel">
          <div className="esc-modo-painel-titulo">
            <StackIcon /> Distribuição das vagas
          </div>
          <div className="esc-modos">
            {['escolha_admin', 'ordem_chegada'].map((m) => (
              <button
                type="button"
                key={m}
                className={`esc-modo-card ${ciclo.modo === m ? 'ativo' : ''}`}
                onClick={() => mudarModo(m)}
                disabled={busy}
              >
                <span className="esc-modo-titulo">
                  {ciclo.modo === m && <CheckCircledIcon />} {MODO_LABEL[m]}
                </span>
                <span className="esc-modo-desc">{MODO_DESC[m]}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="dashboard-tabs esc-abas">
          <button className={`dashboard-tab-btn ${aba === 'calendario' ? 'active' : ''}`} onClick={() => setAba('calendario')}>
            <CalendarIcon /> Calendário
          </button>
          <button className={`dashboard-tab-btn ${aba === 'pendencias' ? 'active' : ''}`} onClick={() => setAba('pendencias')}>
            <LayersIcon /> Pendências {r.pendentes > 0 && <span className="esc-aba-badge">{r.pendentes}</span>}
          </button>
          <button className={`dashboard-tab-btn ${aba === 'pessoas' ? 'active' : ''}`} onClick={() => setAba('pessoas')}>
            <PersonIcon /> Pessoas {ciclo.pessoas?.length > 0 && <span className="esc-aba-badge neutro">{ciclo.pessoas.length}</span>}
          </button>
        </div>

        {aba === 'calendario' && (
          <section className="esc-secao">
            <div className="esc-toolbar">
              <p className="esc-toolbar-dica">Clique num dia para ajustar vagas, marcar feriado, escalar ou decidir pedidos.</p>
              <div className="esc-toolbar-acoes">
                <label className="esc-check">
                  <input type="checkbox" checked={mostrarFdsEfetivo} onChange={(e) => setMostrarFds(e.target.checked)} />
                  Fins de semana
                </label>
                <button className="btn-outline btn-sm" onClick={() => setModalLote(true)} disabled={busy}>
                  Vagas em lote
                </button>
              </div>
            </div>
            <EscalaGrade
              dias={ciclo.dias}
              turnos={turnos}
              mostrar={mostrar}
              renderTurno={renderTurnoAdmin}
              onDiaClick={(dia) => setDiaAbertoId(dia.id)}
            />
            <div className="esc-legenda">
              <span><i className="esc-leg ok" /> confirmado</span>
              <span><i className="esc-leg livre" /> vaga livre</span>
              <span><i className="esc-leg fila" /> aguardando decisão</span>
              <span><i className="esc-leg feriado" /> feriado</span>
            </div>
          </section>
        )}

        {aba === 'pendencias' && (
          <section className="esc-secao">
            <div className="esc-toolbar">
              <p className="esc-toolbar-dica">
                {pendencias.length === 0
                  ? 'Nenhum pedido aguardando decisão.'
                  : `${pendencias.length} pedido(s) aguardando. Confirme, recuse ou abra o dia para ver o turno inteiro.`}
              </p>
              {pendencias.length > 0 && (
                <button className="btn-outline btn-sm" onClick={aprovarTodos} disabled={busy}>
                  <CheckIcon /> Confirmar na ordem de chegada
                </button>
              )}
            </div>
            {pendencias.length > 0 && (
              <div className="table-container">
                <table className="modern-table esc-tabela">
                  <thead>
                    <tr>
                      <th>Dia</th>
                      <th>Turno</th>
                      <th>Militar</th>
                      <th>Turnos no ciclo</th>
                      <th>Pediu em</th>
                      <th>Fila</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendencias.map((p) => {
                      const carga = ciclo.pessoas?.find((x) => x.userId === p.userId);
                      return (
                        <tr key={p.id}>
                          <td className="col-time">
                            <button className="esc-link-btn" onClick={() => setDiaAbertoId(p.dia.id)}>
                              {DIAS_SEMANA_CURTO[p.dia.diaSemana]} {formatDataCurta(p.dia.data)}
                            </button>
                          </td>
                          <td>
                            <span className={`esc-turno-tag esc-turno-cor-${p.turno.turno}`}>{TURNO_LABEL[p.turno.turno]}</span>
                            <small className="esc-muted"> {p.turno.confirmados.length}/{p.turno.vagas}</small>
                          </td>
                          <td className="cell-primary">{nomeEscala(p, true)}</td>
                          <td>{carga ? `${carga.confirmados} confirmado(s)` : '—'}</td>
                          <td className="col-time">{formatDataHora(p.inscritoEm)}</td>
                          <td>{p.posicao}º</td>
                          <td>
                            <span className="esc-pessoa-acoes">
                              <button className="esc-icone-btn ok" title={p.cheio ? 'Sem vaga livre' : 'Confirmar'} disabled={busy || p.cheio} onClick={() => decidir(p, 'confirmado')}>
                                <CheckIcon />
                              </button>
                              <button className="esc-icone-btn perigo" title="Recusar" disabled={busy} onClick={() => decidir(p, 'recusado')}>
                                <Cross2Icon />
                              </button>
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {aba === 'pessoas' && (
          <section className="esc-secao">
            <div className="esc-toolbar">
              <p className="esc-toolbar-dica">Quem já tem turno no ciclo — para distribuir as vagas com equilíbrio.</p>
            </div>
            {(ciclo.pessoas || []).length === 0 ? (
              <div className="empty-state-container">
                <p className="empty-title">Ninguém escalado ainda</p>
                <p className="empty-subtitle">Escale pelo calendário ou aguarde os pedidos dos itinerantes.</p>
              </div>
            ) : (
              <div className="table-container">
                <table className="modern-table esc-tabela">
                  <thead>
                    <tr>
                      <th>Militar</th>
                      <th>Turnos confirmados</th>
                      <th>Aguardando</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ciclo.pessoas.map((p) => (
                      <tr key={p.userId}>
                        <td className="cell-primary">{nomeEscala(p, true)}</td>
                        <td>{p.confirmados}</td>
                        <td>{p.pendentes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </div>

      <CicloFormModal
        aberto={modalCiclo === 'editar'}
        ciclo={ciclo}
        ciclosExistentes={ciclosExistentes}
        busy={busy}
        onClose={() => setModalCiclo(null)}
        onSalvar={editar}
      />

      {diaAberto && (
        <DiaEditorModal
          ciclo={ciclo}
          dia={diaAberto}
          candidatos={candidatos}
          pessoas={ciclo.pessoas}
          busy={busy}
          onClose={() => setDiaAbertoId(null)}
          onSalvarDia={salvarDia}
          onEscalar={escalarPessoa}
          onDecidir={decidir}
        />
      )}

      {modalLote && (
        <div className="modal-overlay" onClick={() => !busy && setModalLote(false)}>
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2>Vagas em lote</h2>
                <p className="esc-modal-sub">Aplica as vagas a todos os dias do ciclo. Feriados ficam de fora.</p>
              </div>
              <button className="modal-close" onClick={() => setModalLote(false)} disabled={busy} aria-label="Fechar">&times;</button>
            </div>
            <div className="form esc-form">
              <div className="esc-dia-vagas">
                {[['manha', 'Manhã', 'vagasManha'], ['tarde', 'Tarde', 'vagasTarde'], ['noite', 'Noite', 'vagasNoite']].map(([key, label, campo]) => (
                  <div key={key} className={`esc-dia-vaga esc-turno-cor-${key}`}>
                    <span>{label}</span>
                    <Stepper value={lote[campo]} onChange={(v) => setLote({ ...lote, [campo]: v })} ariaLabel={`vagas ${label}`} />
                  </div>
                ))}
              </div>
              <div className="form-group">
                <label>Aplicar em</label>
                <select className="role-select" value={lote.escopo} onChange={(e) => setLote({ ...lote, escopo: e.target.value })}>
                  <option value="padrao">Dias do ciclo ({DIAS_OPCAO_LABEL[diasOpcaoDe(ciclo)].toLowerCase()})</option>
                  <option value="uteis">Só segunda a sexta</option>
                  <option value="todos">Todos os dias, inclusive sábado e domingo</option>
                </select>
              </div>
              <small className="form-hint">Reduzir vagas abaixo dos confirmados de um turno é recusado — remova as pessoas antes.</small>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setModalLote(false)} disabled={busy}>Cancelar</button>
                <button className="btn-primary" onClick={aplicarLote} disabled={busy}>{busy ? 'Aplicando…' : 'Aplicar'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        aberto={!!confirm}
        titulo={confirm?.titulo}
        confirmLabel={confirm?.label}
        perigo={confirm?.perigo}
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => confirm?.acao()}
      >
        <p>{confirm?.texto}</p>
      </ConfirmModal>
    </>
  );
};

export default EscalaAdminPage;
